import React from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@codes/ui-kit/components/ui/card';
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

interface ServiceUsageChartProps {
    data: any[];
    colors: string[];
    gradientColors: Array<{ start: string; end: string }>;
}

export const ServiceUsageChart: React.FC<ServiceUsageChartProps> = ({ data, colors, gradientColors }) => {
    const { t } = useTranslation();

    if (data.length === 0) {
        return null;
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-xl font-bold">{t('logs.charts.serviceUsage')}</CardTitle>
                <CardDescription className="text-sm text-gray-600">&nbsp;</CardDescription>
            </CardHeader>
            <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={data} margin={{ top: 0, right: 30, left: 20, bottom: 0 }}>
                        <defs>
                            {gradientColors.map((color, index) => (
                                <linearGradient key={index} id={`gradient${index}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={color.start} stopOpacity={0.8} />
                                    <stop offset="95%" stopColor={color.end} stopOpacity={0.6} />
                                </linearGradient>
                            ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis dataKey="fullName" angle={0} textAnchor="middle" height={30} tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                            }}
                            labelFormatter={value => {
                                const item = data.find(d => d.name === value);
                                return item?.fullName || value;
                            }}
                        />
                        <Legend wrapperStyle={{ paddingTop: '5px' }} />
                        <Bar dataKey={t('logs.charts.apiCallLabel')} radius={[8, 8, 0, 0]}>
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={`url(#gradient${index % gradientColors.length})`} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
};
